import React, { useState, useEffect } from 'react';
import { useLearning } from '../../../context/LearningContext';
import { KnowledgeItem } from '../../../types/learning';
import { v4 as uuidv4 } from 'uuid';
import { formatISO, format, parseISO } from 'date-fns';
import styles from './LearningDebugPanel.module.css';

/**
 * Debug panel for developers to manage KnowledgeItems and learning data
 * This is not intended for end users
 */
const LearningDebugPanel: React.FC = () => {
  const { 
    knowledgeItems, 
    isLoading, 
    knowledgeItemService, 
    spacedRepetitionService,
    learningMetricsService 
  } = useLearning();
  
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'items' | 'create' | 'edit' | 'stats'>('items');
  const [itemsList, setItemsList] = useState<KnowledgeItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<KnowledgeItem | null>(null);
  const [filteredType, setFilteredType] = useState<string>('all');
  const [newItem, setNewItem] = useState<Partial<KnowledgeItem>>({
    id: uuidv4(),
    content: '',
    interval: 0,
    repetition: 0,
    efactor: 2.5,
    lastReviewed: new Date(),
    nextReviewDate: new Date(),
    performanceHistory: [],
    type: 'coordinate'
  });

  // Update local state when knowledge items change
  useEffect(() => {
    if (isOpen && !isLoading) {
      setItemsList([...knowledgeItems]);
    }
  }, [isOpen, knowledgeItems, isLoading]);

  // Reset selected item when tab changes
  useEffect(() => {
    if (activeTab !== 'edit') {
      setSelectedItem(null);
    }
  }, [activeTab]);

  // Helper functions
  const truncateContent = (content: string, maxLength = 30) => {
    return content.length > maxLength ? `${content.substring(0, maxLength)}...` : content;
  };

  const formatDate = (date: Date) => {
    return format(date, 'MM/dd/yyyy');
  };

  const formatDateForInput = (date: Date) => {
    return format(date, 'MM/dd/yyyy');
  };

  const parseInputDate = (dateString: string): Date => {
    try {
      // Try to parse the input date in MM/dd/yyyy format
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1; // Months are 0-indexed in JS Date
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
          const date = new Date(year, month, day);
          return date;
        }
      }
      // Fallback to current date if parsing fails
      return new Date();
    } catch (e) {
      console.error('Error parsing date:', e);
      return new Date();
    }
  };

  const handleDateInputChange = (field: string, value: string, setter: any) => {
    const date = parseInputDate(value);
    
    // Handle both direct setter functions and the handleItemFormChange function
    if (typeof setter === 'function') {
      if (setter.length === 2) {
        // This is the handleItemFormChange function that expects (field, value)
        setter(field, date);
      } else {
        // This is a useState setter function
        setter((prev: any) => ({
          ...prev,
          [field]: date
        }));
      }
    }
  };

  const getPercentage = (value: number, total: number) => {
    return total === 0 ? 0 : Math.round((value / total) * 100);
  };

  const getFilteredItems = () => {
    if (filteredType === 'all') return itemsList;
    return itemsList.filter(item => item.type === filteredType);
  };

  const getItemCountsByType = () => {
    const types = ['coordinate', 'move', 'sequence', 'memory'];
    return types.map(type => ({
      type,
      count: knowledgeItems.filter(item => item.type === type).length
    }));
  };

  // Event handlers
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectItem = (item: KnowledgeItem) => {
    setSelectedItem(item);
    setActiveTab('edit');
  };

  const handleEditItem = (item: KnowledgeItem) => {
    setSelectedItem(item);
    setActiveTab('edit');
  };

  const handleDeleteItem = (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      knowledgeItemService.deleteKnowledgeItem(id);
      setItemsList(prev => prev.filter(item => item.id !== id));
      if (selectedItem?.id === id) {
        setSelectedItem(null);
      }
    }
  };

  const handleDeleteAllItems = () => {
    if (window.confirm('Are you sure you want to delete ALL knowledge items? This cannot be undone!')) {
      itemsList.forEach(item => {
        knowledgeItemService.deleteKnowledgeItem(item.id);
      });
      setItemsList([]);
      setSelectedItem(null);
    }
  };

  const handleCreateItem = (e: React.FormEvent) => {
    e.preventDefault();
    
    let finalItem: KnowledgeItem = {
      ...newItem as KnowledgeItem,
      performanceHistory: [],
      lastReviewed: new Date(),
      nextReviewDate: new Date()
    };
    
    // If it's a move-type item, validate FEN part of content
    if (finalItem.type === 'move' && typeof finalItem.content === 'string') {
      finalItem = knowledgeItemService.validateKnowledgeItem(finalItem);
    }
    
    knowledgeItemService.saveKnowledgeItem(finalItem);
    setItemsList(prev => [...prev, finalItem]);
    resetForm();
    setActiveTab('items');
  };

  const handleUpdateItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    
    let updatedItem = {...selectedItem};
    
    // If it's a move-type item, validate FEN part of content
    if (updatedItem.type === 'move' && typeof updatedItem.content === 'string') {
      updatedItem = knowledgeItemService.validateKnowledgeItem(updatedItem);
    }
    
    knowledgeItemService.saveKnowledgeItem(updatedItem);
    setItemsList(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item));
    
    // Return to the items tab after updating
    setActiveTab('items');
  };

  const handleAddGrade = (item: KnowledgeItem, grade: number) => {
    const updatedItem = spacedRepetitionService.updateKnowledgeItemReview(item, grade as any);
    setSelectedItem(updatedItem);
    knowledgeItemService.saveKnowledgeItem(updatedItem);
    setItemsList(prev => prev.map(i => i.id === item.id ? updatedItem : i));
  };

  const resetForm = () => {
    setNewItem({
      id: uuidv4(),
      content: '',
      interval: 0,
      repetition: 0,
      efactor: 2.5,
      lastReviewed: new Date(),
      nextReviewDate: new Date(),
      performanceHistory: [],
      type: 'coordinate'
    });
  };

  const handleResetAllStats = () => {
    if (window.confirm('Reset all learning statistics? This will clear all performance history.')) {
      const resetItems = knowledgeItems.map(item => ({
        ...item,
        interval: 0,
        repetition: 0,
        efactor: 2.5,
        performanceHistory: [],
        nextReviewDate: new Date()
      }));
      
      resetItems.forEach(item => {
        knowledgeItemService.saveKnowledgeItem(item);
      });
      
      setItemsList(resetItems);
      alert('All learning statistics have been reset.');
    }
  };

  const handleItemFormChange = (field: string, value: any) => {
    if (selectedItem) {
      setSelectedItem({
        ...selectedItem,
        [field]: value
      });
    }
  };

  const generateSampleData = () => {
    if (window.confirm('Generate sample learning data? This will add several test items.')) {
      const sampleItems = createSampleItems();
      sampleItems.forEach(item => {
        knowledgeItemService.saveKnowledgeItem(item);
      });
      setItemsList(prev => [...prev, ...sampleItems]);
      alert(`Added ${sampleItems.length} sample items`);
    }
  };

  // Helper for creating sample test data
  const createSampleItems = (): KnowledgeItem[] => {
    const types: Array<KnowledgeItem['type']> = ['coordinate', 'move', 'sequence', 'memory'];
    
    // Sample valid FEN positions for move items
    const sampleFENs = [
      // Standard starting position
      'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      // Position after e4
      'rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq - 0 1',
      // Position after e4 e5
      'rnbqkbnr/pppp1ppp/8/4p3/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2',
      // Position with knights developed
      'rnbqkbnr/pppp1ppp/8/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 1 2'
    ];
    
    // Sample valid moves for each position
    const sampleMoves = [
      'e4', // From starting position
      'e5', // After e4
      'Nf3', // After e4 e5
      'Nc6'  // After knights developed
    ];
    
    // Human-readable descriptions for move items
    const moveDescriptions = [
      "King's Pawn Opening (play e4)",
      "Respond to e4 with e5",
      "Develop knight to f3",
      "Respond with knight to c6"
    ];
    
    return Array.from({ length: 8 }, (_, i) => {
      const type = types[i % types.length];
      const now = new Date();
      const nextReviewDate = new Date();
      nextReviewDate.setDate(now.getDate() + i);
      
      // Create base item
      let baseItem: KnowledgeItem = {
        id: uuidv4(),
        content: `Sample ${type} item ${i + 1}`,
        type,
        interval: i,
        repetition: Math.floor(i / 2),
        efactor: 2.5 - (i * 0.1),
        lastReviewed: now,
        nextReviewDate,
        performanceHistory: Array.from({ length: i % 5 }, () => Math.floor(Math.random() * 6))
      };
      
      // Add move-specific content for move items
      if (type === 'move') {
        const fenIndex = i % sampleFENs.length;
        const fen = sampleFENs[fenIndex];
        const move = sampleMoves[fenIndex];
        // Replace the raw FEN|move with a human-readable description
        baseItem.content = moveDescriptions[fenIndex];
        
        // Store the actual FEN and move in separate fields
        // These will be custom fields not part of the standard KnowledgeItem type
        (baseItem as any).fen = fen;
        (baseItem as any).targetMove = move;
      }
      
      // Add memory-specific fields
      if (type === 'memory') {
        return {
          ...baseItem,
          mode: Math.random() > 0.5 ? 'coordinate' : 'move',
          n: 1 + (i % 3),
          length: 10 + (i % 10)
        };
      }
      
      return baseItem;
    });
  };

  // Render tab content
  const renderItemsTab = () => (
    <div className={styles.itemsContainer}>
      <div className={styles.filterBar}>
        <label>
          Filter by type:
          <select 
            value={filteredType} 
            onChange={(e) => setFilteredType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="coordinate">Coordinate</option>
            <option value="move">Move</option>
            <option value="sequence">Sequence</option>
            <option value="memory">Memory</option>
          </select>
        </label>
      </div>
      
      <div className={styles.itemList} data-testid="item-list">
        {getFilteredItems().length === 0 ? (
          <div className={styles.noItems}>
            No knowledge items found.
          </div>
        ) : (
          getFilteredItems().map(item => (
            <div 
              key={item.id} 
              className={`${styles.itemRow} ${selectedItem?.id === item.id ? styles.selectedItem : ''}`}
              onClick={() => handleSelectItem(item)}
              data-testid="item-row"
            >
              <div className={styles.itemInfo}>
                <span className={styles.itemTitle}>{truncateContent(item.content)}</span>
                <span className={styles.itemDetails}>
                  {item.type} | Interval: {item.interval} | E-Factor: {item.efactor.toFixed(2)}
                </span>
                <span className={styles.nextReview}>
                  Next review: {formatDate(item.nextReviewDate)}
                </span>
              </div>
              <div className={styles.itemActions}>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleEditItem(item); }}
                  className={styles.editButton}
                >
                  Edit
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className={styles.buttonContainer}>
        <button onClick={handleDeleteAllItems} className={styles.dangerButton}>
          Delete All Items
        </button>
        <button onClick={generateSampleData} className={styles.actionButton}>
          Add Sample Data
        </button>
      </div>
    </div>
  );

  const renderCreateTab = () => (
    <div className={styles.formContainer}>
      <h4>Create New Knowledge Item</h4>
      <form onSubmit={handleCreateItem}>
        <div className={styles.formRow}>
          <label htmlFor="content">Content:</label>
          <textarea
            id="content"
            value={newItem.content}
            onChange={(e) => setNewItem({...newItem, content: e.target.value})}
            required
          />
        </div>
        
        <div className={styles.formRow}>
          <label htmlFor="type">Type:</label>
          <select
            id="type"
            value={newItem.type}
            onChange={(e) => setNewItem({...newItem, type: e.target.value as any})}
            required
          >
            <option value="coordinate">Coordinate</option>
            <option value="move">Move</option>
            <option value="sequence">Sequence</option>
            <option value="memory">Memory</option>
          </select>
        </div>
        
        {/* Additional fields based on type */}
        {newItem.type === 'memory' && (
          <>
            <div className={styles.formRow}>
              <label htmlFor="mode">N-Back Mode:</label>
              <select
                id="mode"
                value={newItem.mode}
                onChange={(e) => setNewItem({...newItem, mode: e.target.value as any})}
              >
                <option value="coordinate">Coordinate</option>
                <option value="move">Move</option>
              </select>
            </div>
            <div className={styles.formRow}>
              <label htmlFor="n">N (steps back):</label>
              <input
                id="n"
                type="number"
                min="1"
                max="3"
                value={newItem.n || 1}
                onChange={(e) => setNewItem({...newItem, n: parseInt(e.target.value)})}
              />
            </div>
            <div className={styles.formRow}>
              <label htmlFor="length">Sequence Length:</label>
              <input
                id="length"
                type="number"
                min="5"
                max="20"
                value={newItem.length || 10}
                onChange={(e) => setNewItem({...newItem, length: parseInt(e.target.value)})}
              />
            </div>
          </>
        )}
        
        <div className={styles.formRow}>
          <label htmlFor="interval">Interval (days):</label>
          <input
            id="interval"
            type="number"
            min="0"
            value={newItem.interval}
            onChange={(e) => setNewItem({...newItem, interval: parseInt(e.target.value)})}
            required
          />
        </div>
        
        <div className={styles.formRow}>
          <label htmlFor="repetition">Repetition Count:</label>
          <input
            id="repetition"
            type="number"
            min="0"
            value={newItem.repetition}
            onChange={(e) => setNewItem({...newItem, repetition: parseInt(e.target.value)})}
            required
          />
        </div>
        
        <div className={styles.formRow}>
          <label htmlFor="efactor">E-Factor:</label>
          <input
            id="efactor"
            type="number"
            min="1.3"
            max="2.5"
            step="0.1"
            value={newItem.efactor}
            onChange={(e) => setNewItem({...newItem, efactor: parseFloat(e.target.value)})}
            required
          />
        </div>
        
        <div className={styles.formRow}>
          <label htmlFor="nextReview">Next Review Date:</label>
          <input
            id="nextReview"
            type="text"
            placeholder="MM/DD/YYYY"
            value={formatDateForInput(newItem.nextReviewDate || new Date())}
            onChange={(e) => handleDateInputChange('nextReviewDate', e.target.value, setNewItem)}
            required
          />
          <small className={styles.helperText}>Format: MM/DD/YYYY</small>
        </div>
        
        <div className={styles.buttonContainer}>
          <button type="submit" className={styles.createButton}>
            Create Item
          </button>
          <button 
            type="button" 
            onClick={resetForm} 
            className={styles.resetButton}
          >
            Reset Form
          </button>
        </div>
      </form>
    </div>
  );

  const renderEditTab = () => {
    if (!selectedItem) {
      return (
        <div className={styles.noSelection}>
          <p>Please select an item to edit from the Browse Items tab</p>
        </div>
      );
    }
    
    return (
      <div className={styles.formContainer}>
        <h4>Edit Knowledge Item</h4>
        <form onSubmit={handleUpdateItem}>
          <div className={styles.formRow}>
            <label htmlFor="edit-id">ID:</label>
            <input
              id="edit-id"
              type="text"
              value={selectedItem.id}
              readOnly
              disabled
            />
          </div>

          <div className={styles.formRow}>
            <label htmlFor="edit-content">Content:</label>
            <textarea
              id="edit-content"
              value={selectedItem.content}
              onChange={(e) => handleItemFormChange('content', e.target.value)}
              required
            />
          </div>
          
          <div className={styles.formRow}>
            <label htmlFor="edit-type">Type:</label>
            <select
              id="edit-type"
              value={selectedItem.type}
              onChange={(e) => handleItemFormChange('type', e.target.value)}
              required
            >
              <option value="coordinate">Coordinate</option>
              <option value="move">Move</option>
              <option value="sequence">Sequence</option>
              <option value="memory">Memory</option>
            </select>
          </div>
          
          {/* Additional fields based on type */}
          {selectedItem.type === 'memory' && (
            <>
              <div className={styles.formRow}>
                <label htmlFor="edit-mode">N-Back Mode:</label>
                <select
                  id="edit-mode"
                  value={selectedItem.mode}
                  onChange={(e) => handleItemFormChange('mode', e.target.value)}
                >
                  <option value="coordinate">Coordinate</option>
                  <option value="move">Move</option>
                </select>
              </div>
              <div className={styles.formRow}>
                <label htmlFor="edit-n">N (steps back):</label>
                <input
                  id="edit-n"
                  type="number"
                  min="1"
                  max="3"
                  value={selectedItem.n || 1}
                  onChange={(e) => handleItemFormChange('n', parseInt(e.target.value))}
                />
              </div>
              <div className={styles.formRow}>
                <label htmlFor="edit-length">Sequence Length:</label>
                <input
                  id="edit-length"
                  type="number"
                  min="5"
                  max="20"
                  value={selectedItem.length || 10}
                  onChange={(e) => handleItemFormChange('length', parseInt(e.target.value))}
                />
              </div>
            </>
          )}
          
          <div className={styles.formRow}>
            <label htmlFor="edit-interval">Interval (days):</label>
            <input
              id="edit-interval"
              type="number"
              min="0"
              value={selectedItem.interval}
              onChange={(e) => handleItemFormChange('interval', parseInt(e.target.value))}
              required
            />
          </div>
          
          <div className={styles.formRow}>
            <label htmlFor="edit-repetition">Repetition Count:</label>
            <input
              id="edit-repetition"
              type="number"
              min="0"
              value={selectedItem.repetition}
              onChange={(e) => handleItemFormChange('repetition', parseInt(e.target.value))}
              required
            />
          </div>
          
          <div className={styles.formRow}>
            <label htmlFor="edit-efactor">E-Factor:</label>
            <input
              id="edit-efactor"
              type="number"
              min="1.3"
              max="2.5"
              step="0.1"
              value={selectedItem.efactor}
              onChange={(e) => handleItemFormChange('efactor', parseFloat(e.target.value))}
              required
            />
          </div>
          
          <div className={styles.formRow}>
            <label htmlFor="edit-lastReviewed">Last Reviewed:</label>
            <input
              id="edit-lastReviewed"
              type="text"
              placeholder="MM/DD/YYYY"
              value={formatDateForInput(selectedItem.lastReviewed)}
              onChange={(e) => handleDateInputChange('lastReviewed', e.target.value, handleItemFormChange)}
              required
            />
            <small className={styles.helperText}>Format: MM/DD/YYYY</small>
          </div>
          
          <div className={styles.formRow}>
            <label htmlFor="edit-nextReview">Next Review Date:</label>
            <input
              id="edit-nextReview"
              type="text"
              placeholder="MM/DD/YYYY"
              value={formatDateForInput(selectedItem.nextReviewDate)}
              onChange={(e) => handleDateInputChange('nextReviewDate', e.target.value, handleItemFormChange)}
              required
            />
            <small className={styles.helperText}>Format: MM/DD/YYYY</small>
          </div>
          
          <div className={styles.formRow}>
            <label>Performance History:</label>
            <div className={styles.historyContainer}>
              {selectedItem.performanceHistory.length === 0 ? (
                <span>No performance data yet</span>
              ) : (
                selectedItem.performanceHistory.map((grade, index) => (
                  <span 
                    key={index} 
                    className={`${styles.gradeChip} ${styles[`grade${grade}`]}`}
                  >
                    {grade}
                  </span>
                ))
              )}
            </div>
          </div>
          
          <div className={styles.formRow}>
            <label>Add Performance Grade:</label>
            <div className={styles.gradeButtons}>
              {[0, 1, 2, 3, 4, 5].map(grade => (
                <button 
                  key={grade}
                  type="button"
                  className={`${styles.gradeButton} ${styles[`grade${grade}`]}`}
                  onClick={() => handleAddGrade(selectedItem, grade)}
                >
                  {grade}
                </button>
              ))}
            </div>
          </div>
          
          <div className={styles.buttonContainer}>
            <button type="submit" className={styles.updateButton}>
              Update Item
            </button>
            <button 
              type="button" 
              onClick={() => handleDeleteItem(selectedItem.id)} 
              className={styles.deleteButton}
            >
              Delete Item
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderStatsTab = () => {
    const distribution = !isLoading && knowledgeItems.length > 0
      ? learningMetricsService.calculateSkillDistribution(knowledgeItems)
      : {
          coordinate: 0,
          move: 0,
          sequence: 0,
          memory: 0
        };
    
    // Calculate total items for each category for visualization
    const newItemsCount = knowledgeItems.filter(item => item.repetition === 0).length;
    const masteredItemsCount = knowledgeItems.filter(item => item.interval >= 21).length;
    const learningItemsCount = knowledgeItems.length - newItemsCount - masteredItemsCount;
    
    // Calculate retention rate
    const retentionRate = !isLoading && knowledgeItems.length > 0
      ? learningMetricsService.calculateRetentionRate(knowledgeItems)
      : { percentRetained: 0, totalReviews: 0, correctReviews: 0 };
    
    // Get the weak areas
    const weakAreas = !isLoading && knowledgeItems.length > 0
      ? learningMetricsService.identifyWeakAreas(knowledgeItems)
      : [];
    
    return (
      <div className={styles.tabContent}>
        <h2>Learning Analytics</h2>
        
        <div className={styles.statsBlock}>
          <h3>Item Statistics</h3>
          <div className={styles.statRow}>
            <div className={styles.statItem}>
              <strong>Total Items:</strong> {knowledgeItems.length}
            </div>
            
            <div className={styles.statItem}>
              <strong>Total Reviews:</strong> {retentionRate.totalReviews}
            </div>
            
            <div className={styles.statItem}>
              <strong>Retention Rate:</strong> {retentionRate.percentRetained}%
            </div>
          </div>
        </div>
        
        <div className={styles.statsBlock}>
          <h3>Learning Status Distribution</h3>
          <div className={styles.distributionBar}>
            <div 
              className={`${styles.distributionSegment} ${styles.newSegment}`}
              style={{ 
                width: `${getPercentage(newItemsCount, knowledgeItems.length)}%` 
              }}
              title={`New: ${newItemsCount} items`}
            ></div>
            <div 
              className={`${styles.distributionSegment} ${styles.learningSegment}`}
              style={{ 
                width: `${getPercentage(learningItemsCount, knowledgeItems.length)}%` 
              }}
              title={`Learning: ${learningItemsCount} items`}
            ></div>
            <div 
              className={`${styles.distributionSegment} ${styles.masteredSegment}`}
              style={{ 
                width: `${getPercentage(masteredItemsCount, knowledgeItems.length)}%` 
              }}
              title={`Mastered: ${masteredItemsCount} items`}
            ></div>
          </div>
          
          <div className={styles.distributionLegend}>
            <div className={styles.legendItem}>
              <span className={`${styles.legendColor} ${styles.newColor}`}></span>
              New: {newItemsCount}
            </div>
            
            <div className={styles.legendItem}>
              <span className={`${styles.legendColor} ${styles.learningColor}`}></span>
              Learning: {learningItemsCount}
            </div>
            
            <div className={styles.legendItem}>
              <span className={`${styles.legendColor} ${styles.masteredColor}`}></span>
              Mastered: {masteredItemsCount}
            </div>
          </div>
        </div>
        
        <div className={styles.statsBlock}>
          <h3>Skill Category Distribution</h3>
          <div className={styles.skillCategoryGrid}>
            {Object.entries(distribution).map(([category, score]) => (
              <div key={category} className={styles.skillCategoryItem}>
                <div className={styles.skillCategoryName}>{category}</div>
                <div className={styles.skillCategoryBar}>
                  <div 
                    className={styles.skillCategoryProgress} 
                    style={{ width: `${score}%` }}
                  ></div>
                </div>
                <div className={styles.skillCategoryScore}>{score}%</div>
              </div>
            ))}
          </div>
        </div>
        
        {weakAreas.length > 0 && (
          <div className={styles.statsBlock}>
            <h3>Areas Needing Improvement</h3>
            <div className={styles.weakAreasList}>
              {weakAreas.map(area => (
                <div key={area.category} className={styles.weakAreaItem}>
                  <div className={styles.weakAreaName}>{area.category}</div>
                  <div className={styles.weakAreaBar}>
                    <div 
                      className={styles.weakAreaProgress} 
                      style={{ 
                        width: `${area.performance}%`,
                        backgroundColor: area.performance < 40 ? '#D14836' : 
                                        area.performance < 60 ? '#BF9B30' : '#4a7c59'
                      }}
                    ></div>
                  </div>
                  <div className={styles.weakAreaScore}>{area.performance}%</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className={styles.actionButtons}>
          <button 
            className={`${styles.debugButton} ${styles.dangerButton}`}
            onClick={handleResetAllStats}
          >
            Reset All Learning Statistics
          </button>
        </div>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'items':
        return renderItemsTab();
      case 'create':
        return renderCreateTab();
      case 'edit':
        return renderEditTab();
      case 'stats':
        return renderStatsTab();
      default:
        return renderItemsTab();
    }
  };

  return (
    <div className={styles.debugContainer}>
      {/* Debug toggle button */}
      <button 
        className={styles.debugToggle}
        onClick={togglePanel}
        title="Learning Debug Mode"
      >
        📚
      </button>

      {/* Debug panel */}
      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>Learning System Debug Panel</h3>
            <button onClick={togglePanel} className={styles.closeButton}>×</button>
          </div>

          {/* Tab navigation */}
          <div className={styles.tabBar}>
            <button 
              className={`${styles.tabButton} ${activeTab === 'items' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('items')}
            >
              Browse Items
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'create' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('create')}
            >
              Create Item
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'edit' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('edit')}
              disabled={!selectedItem}
            >
              Edit Item
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'stats' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              Stats
            </button>
          </div>
          
          {/* Tab content */}
          {renderTabContent()}
        </div>
      )}
    </div>
  );
};

export default LearningDebugPanel; 